import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import defaultImg from '../../asset/project/default_1.png';
import { CurrencyCode, WorkType, usesWorkDuration } from '../../shared/enum/EnumRepository';
import { WorkData, convertWorkSizeToString, getMainSizeData } from '../../shared/dto/EntityRepository';
import { useWorkViewStore } from '../../shared/store/WorkViewStore';
import { useWorkPropertyVisible } from '../../shared/hooks/useApi/useWorkPropertyVisible';
import { useWorkContentModeStore } from '../../shared/store/workContentModeStore';
import { resolveWorkMaterial, resolveWorkSizeText, resolveWorkTitle } from '../../shared/util/workDisplayText';
import { resolveWorkArtistName } from '../../shared/util/workArtist';
import { useOptimizedImageUrl } from '../../shared/hooks/useOptimizedImageUrl';
import { getCurrencySymbol, renderingPrice } from '../../shared/util/renderingPrice';
import { shrinkString } from '../../shared/util/shrinkString';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useWorkListMarqueeStore } from '../../shared/store/workListMarqueeStore';
import { useWorkIndexStore } from '../../shared/store/WorkIndexStore';
import BlockWithVisible from './BlockWithVisible';
import { useObjectUrl } from '../../shared/hooks/useObjectUrl';

export interface WorkInfoRowProps {
  data: WorkData;
  handleWorkClick: (data: WorkData, event: React.MouseEvent) => void;
}

export interface WorkInfoRowRenderData {
  rowWork: WorkData;
  title: string;
  artistName: string;
  isHover: boolean;
  isActive: boolean;
}

interface WorkInfoRowBaseProps extends WorkInfoRowProps {
  children: (data: WorkInfoRowRenderData) => React.ReactNode;
}

const WorkInfoRowBase: React.FC<WorkInfoRowBaseProps> = ({ data, handleWorkClick, children }) => {
  const workId = String(data.id);
  const { isEditMode } = useEditMode();
  // Per-row selectors: only this row re-renders when its selection/active snapshot changes.
  const activeWorkForRow = useWorkViewStore((s) =>
    s.activeWork && String(s.activeWork.id) === workId ? s.activeWork : null,
  );
  const pendingImageFile = useWorkViewStore((s) =>
    s.activeWork && String(s.activeWork.id) === workId ? s.pendingImage?.file ?? null : null,
  );
  const isInMulti = useWorkViewStore((s) =>
    s.multiSelectedWork.some((work) => String(work.id) === workId),
  );
  const { workPropertyVisible } = useWorkPropertyVisible();
  const workContentMode = useWorkContentModeStore((state) => state.workContentMode);
  const isIndexingMode = useWorkIndexStore((s) => s.isIndexingMode);
  const isInIndexing = useWorkIndexStore((s) =>
    s.selectedIndexingWorkList.some((work) => String(work.id) === workId),
  );
  const addSelectedIndexingWork = useWorkIndexStore((s) => s.addSelectedIndexingWork);
  const [hovered, setHovered] = useState(false);
  const imgContainerRef = useRef<HTMLDivElement>(null);
  const isHitByMarquee = useWorkListMarqueeStore((state) =>
    state.hitWorks.some((work) => String(work.id) === workId),
  );

  const isRowActive = activeWorkForRow != null;
  const rowWork = activeWorkForRow ?? data;
  const effectiveOriginUrl = isRowActive
    ? activeWorkForRow.uploadFile?.originUrl ?? data.uploadFile?.originUrl ?? ''
    : data.uploadFile?.originUrl ?? '';
  const optimizedImageUrl = useOptimizedImageUrl(effectiveOriginUrl, imgContainerRef);
  const pendingImageUrl = useObjectUrl(pendingImageFile);
  const imageUrl = pendingImageUrl || optimizedImageUrl;
  const engVisible = workPropertyVisible?.engVisible !== false;

  const displayTitle = useMemo(
    () => resolveWorkTitle(rowWork, workContentMode, engVisible),
    [rowWork, workContentMode, engVisible],
  );
  const displayMaterial = useMemo(
    () => resolveWorkMaterial(rowWork, workContentMode, engVisible),
    [rowWork, workContentMode, engVisible],
  );
  const title = useMemo(() => shrinkString(displayTitle, 30), [displayTitle]);
  const artistName = useMemo(
    () => shrinkString(resolveWorkArtistName(rowWork.artist, workContentMode, engVisible), 30),
    [engVisible, rowWork.artist, workContentMode],
  );
  const sizeString = useMemo(() => {
    if (usesWorkDuration(rowWork.workType)) return rowWork.duration || '-';
    return convertWorkSizeToString(
      getMainSizeData(rowWork.sizes, rowWork.mainSizeUnit),
      rowWork.dimensionsType,
      resolveWorkSizeText(rowWork),
      'Variable',
    );
  }, [rowWork]);
  const renderedPrice = useMemo(() => {
    if (rowWork.isPOV) return 'POA';
    const amount = renderingPrice(rowWork.price);
    if (!amount) return '-';
    const currency = rowWork.price.currency ?? CurrencyCode.USD;
    return `${getCurrencySymbol(currency)} ${amount}`;
  }, [rowWork.price, rowWork.isPOV]);
  const collection = useMemo(() => shrinkString(data.collection, 20), [data.collection]);

  const isHover = hovered || isHitByMarquee;
  const isActive = isIndexingMode ? isInIndexing : (isRowActive || isInMulti);

  const handleClick = (event: React.MouseEvent) => {
    if (isIndexingMode) addSelectedIndexingWork(data);
    else handleWorkClick(data, event);
  };

  if (!workPropertyVisible) return null;

  return (
    <Row
      data-work-id={data.id}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      $isEditMode={isEditMode}
      $isPublic={rowWork.isPublic !== false}
    >
      <ImageBlock $isHover={isHover} $isActive={isActive}>
        <ImgWrapper ref={imgContainerRef}>
          <WorkImage src={imageUrl || defaultImg} alt={displayTitle} />
        </ImgWrapper>
      </ImageBlock>
      {children({ rowWork, title, artistName, isHover, isActive })}
      <BlockWithVisible width="1.5" value={sizeString} isHover={isHover} isActive={isActive} />
      <BlockWithVisible
        width="2"
        value={displayMaterial || '-'}
        isHover={isHover}
        isActive={isActive}
      />
      <BlockWithVisible
        width="0.8"
        value={data.prodYear === '' || data.prodYear === null ? '-' : data.prodYear}
        isHover={isHover}
        isActive={isActive}
      />
      <BlockWithVisible
        width="1.2"
        field="workType"
        value={data.workType === WorkType.NONE ? '-' : data.workType}
        isHover={isHover}
        isActive={isActive}
      />
      <BlockWithVisible
        width="1"
        field="price"
        value={renderedPrice}
        isHover={isHover}
        isActive={isActive}
      />
      <BlockWithVisible
        width="1"
        field="collection"
        value={data.collection === '' ? '-' : collection}
        isHover={isHover}
        isActive={isActive}
      />
    </Row>
  );
};

export default WorkInfoRowBase;

const Row = styled.article<{ $isEditMode: boolean; $isPublic: boolean }>`
  position: relative;
  display: flex;
  width: 100%;
  height: 40px;
  min-height: 40px;
  user-select: ${({ $isEditMode }) => ($isEditMode ? 'none' : 'auto')};
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
  opacity: ${({ $isPublic }) => (!$isPublic) ? 0.6 : 1};
`;

const ImageBlock = styled.div<{ $isHover: boolean; $isActive: boolean }>`
  height: 100%;
  flex: 0.5;
  min-width: 40px;
  max-width: 50px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background-color: ${({ theme, $isHover, $isActive }) =>
    $isActive ? theme.colors.color_Gray_06 : $isHover ? theme.colors.color_Gray_07 : 'transparent'};
  user-select: none;
  cursor: pointer;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const ImgWrapper = styled.div`
  width: 100%;
  height: 84%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-right: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const WorkImage = styled.img`
  width: 90%;
  height: 96%;
  object-fit: contain;
`;

export const PrimaryBlock = styled.div<{ $isHover: boolean; $isActive: boolean }>`
  flex: 2.5;
  min-width: 0;
  display: flex;
  align-items: center;
  padding-left: 10px;
  text-decoration: ${({ $isActive }) => ($isActive ? 'underline' : 'none')};
  background-color: ${({ theme, $isHover, $isActive }) =>
    $isActive ? theme.colors.color_Gray_06 : $isHover ? theme.colors.color_Gray_07 : 'transparent'};
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  cursor: pointer;
`;

export const PrimaryText = styled.div`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
